import { useState } from 'react';
import '../../css/GetHelpContent.css';

const MODULE_OPTIONS = [
    'Equipment',
    'Forms',
    'Inspections',
    'Issues',
    'Preventive Maintenance',
    'Tire Management',
    'Safety & Training',
    'Cost Tracking',
    'Reports',
    'Resources',
    'Users',
    'Regions',
    'Settings',
    'Other',
];

const IMPORTANCE_OPTIONS = [
    { value: 'nice-to-have', label: 'Nice to have, would use occasionally' },
    { value: 'would-use-regularly', label: 'I would use this regularly' },
    { value: 'critical', label: 'Critical — blocking my work today' },
];

interface RequestFeatureModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: { request: string; problem: string; workaround: string; module: string; importance: string }) => void;
}

export const RequestFeatureModal = ({ isOpen, onClose, onSubmit }: RequestFeatureModalProps) => {
    const [request, setRequest] = useState('');
    const [problem, setProblem] = useState('');
    const [workaround, setWorkaround] = useState('');
    const [module, setModule] = useState('');
    const [importance, setImportance] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!isOpen) return null;

    const handleClose = () => {
        setRequest('');
        setProblem('');
        setWorkaround('');
        setModule('');
        setImportance('');
        onClose();
    };

    const handleSubmit = async () => {
        if (!request.trim()) return;
        setIsSubmitting(true);
        await onSubmit({
            request: request.trim(),
            problem: problem.trim(),
            workaround: workaround.trim(),
            module: module || 'Not specified',
            importance: importance || 'Not specified',
        });
        setIsSubmitting(false);
        setRequest('');
        setProblem('');
        setWorkaround('');
        setModule('');
        setImportance('');
    };

    return (
        <div className="gh-modal-overlay" onClick={handleClose}>
            <div className="gh-modal" onClick={(e) => e.stopPropagation()}>
                <div className="gh-modal-header">
                    <h2 className="gh-modal-title">Request a Feature</h2>
                    <button className="gh-modal-close" onClick={handleClose} aria-label="Close">×</button>
                </div>
                <div className="gh-modal-body">
                    <p className="gh-modal-hint">
                        Got an idea that would make TotalEquipIQ better? We read every request and prioritize what our customers ask for most.
                    </p>

                    <div className="gh-field">
                        <label className="gh-field-label">
                            What is the request? <span className="gh-required">*</span>
                        </label>
                        <input
                            type="text"
                            className="gh-field-input"
                            placeholder="e.g. Export Inspections to Excel"
                            value={request}
                            onChange={(e) => setRequest(e.target.value)}
                        />
                    </div>

                    <div className="gh-field">
                        <label className="gh-field-label">What problem would this solve?</label>
                        <textarea
                            className="gh-field-textarea"
                            placeholder="What are you trying to do today that's hard, slow, or not possible? The more specific, the better."
                            value={problem}
                            onChange={(e) => setProblem(e.target.value)}
                        />
                    </div>

                    <div className="gh-field">
                        <label className="gh-field-label">How are you working around it now? (optional)</label>
                        <textarea
                            className="gh-field-textarea"
                            placeholder="What do you do today instead? Spreadsheet, paper, another tool, nothing?"
                            value={workaround}
                            onChange={(e) => setWorkaround(e.target.value)}
                        />
                    </div>

                    <div className="gh-field">
                        <label className="gh-field-label">Which module?</label>
                        <select
                            className="gh-field-select"
                            value={module}
                            onChange={(e) => setModule(e.target.value)}
                        >
                            <option value="">Select a module...</option>
                            {MODULE_OPTIONS.map(m => (
                                <option key={m} value={m}>{m}</option>
                            ))}
                        </select>
                    </div>

                    <div className="gh-field">
                        <label className="gh-field-label">How important is this to you?</label>
                        <div className="gh-importance-options">
                            {IMPORTANCE_OPTIONS.map(opt => (
                                <label key={opt.value} className="gh-radio-label">
                                    <input
                                        type="radio"
                                        name="importance"
                                        value={opt.value}
                                        checked={importance === opt.value}
                                        onChange={(e) => setImportance(e.target.value)}
                                    />
                                    {opt.label}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="gh-modal-footer">
                    <button className="gh-btn gh-btn--cancel" onClick={handleClose}>Cancel</button>
                    <button
                        className="gh-btn gh-btn--submit-feature"
                        disabled={!request.trim() || isSubmitting}
                        onClick={handleSubmit}
                    >
                        {isSubmitting ? 'Sending...' : 'Send Request'}
                    </button>
                </div>
            </div>
        </div>
    );
};
